import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { ConfirmationModalShell } from '@/shared/components/confirmation'
import { confirmationBackdropSx } from '@/shared/components/confirmation/confirmationModalStyles'
import {
  NON_QUALIFIED_PROMPT_BENEFITS,
  NON_QUALIFIED_PROMPT_BODY,
  NON_QUALIFIED_PROMPT_GOT_IT,
  NON_QUALIFIED_PROMPT_TITLE,
} from '../lib/nonQualifiedCampaignPrompt'

interface Props {
  open: boolean
  onGotIt: () => void
}

/** Layout tokens aligned with non-qualified campaign Figma modal (460px). */
const MODAL_WIDTH = 460
const PRIMARY = '#008CD0'
const TITLE = '#0D1B2A'
const BODY = '#62666F'

const primaryButtonSx = {
  textTransform: 'none' as const,
  width: '100%',
  bgcolor: PRIMARY,
  color: '#FFFFFF',
  borderRadius: '8px',
  height: 48,
  minHeight: 48,
  py: 0,
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  boxShadow: '0px 6px 12px -3px #BAE6FD, 0px 2px 4px -2px #BAE6FD',
  '&:hover': {
    bgcolor: '#0077B3',
    boxShadow: '0px 6px 12px -3px #BAE6FD, 0px 2px 4px -2px #BAE6FD',
  },
}

export function NonQualifiedCampaignPrompt({ open, onGotIt }: Readonly<Props>) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onGotIt()
      }}
      dialogTestId="non-qualified-campaign-prompt"
      ariaLabelledBy="non-qualified-campaign-title"
      ariaDescribedBy="non-qualified-campaign-description"
      backdropSx={confirmationBackdropSx}
      paperSx={{
        width: { xs: 'calc(100% - 32px)', sm: MODAL_WIDTH },
        maxWidth: MODAL_WIDTH,
        borderRadius: '14px',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.18)',
        bgcolor: '#FFFFFF',
      }}
    >
      <Box sx={{ boxSizing: 'border-box', width: '100%', px: '24px', pt: '22px', pb: '22px' }}>
        <Typography
          id="non-qualified-campaign-title"
          component="h2"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 700,
            fontSize: '20px',
            lineHeight: '26px',
            color: TITLE,
            m: 0,
            mb: '10px',
          }}
        >
          {NON_QUALIFIED_PROMPT_TITLE}
        </Typography>

        <Typography
          id="non-qualified-campaign-description"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            color: BODY,
            m: 0,
            mb: '12px',
          }}
        >
          {NON_QUALIFIED_PROMPT_BODY}
        </Typography>

        <Box
          component="ul"
          sx={{ m: 0, mb: '24px', pl: '20px' }}
          data-testid="non-qualified-campaign-benefits"
        >
          {NON_QUALIFIED_PROMPT_BENEFITS.map((benefit) => (
            <Typography
              key={benefit}
              component="li"
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '22px',
                color: BODY,
              }}
            >
              {benefit}
            </Typography>
          ))}
        </Box>

        <Button
          variant="contained"
          onClick={onGotIt}
          data-testid="non-qualified-campaign-got-it"
          sx={primaryButtonSx}
        >
          {NON_QUALIFIED_PROMPT_GOT_IT}
        </Button>
      </Box>
    </ConfirmationModalShell>
  )
}
